import {
  DEFAULT_CROP_RECT,
  DEFAULT_IMAGE_FRAME,
  clampCropInsideImage,
  fitCropRectToAspect,
  hostFramesToSourceRect,
  isFullCropRect,
  normalizeHostCropFrame,
  normalizeImageFrame,
} from "../../../components/shared/imageToolbar/imageCropModel";
import {
  CROP_OUTPUT_FORMATS,
  CROP_ZOOM_DEFAULT,
  CROP_ZOOM_MAX,
  CROP_ZOOM_MIN,
  clampCropInsideHostViewport,
  clampCropInsideVisibleImage,
  clampCropZoom,
  clampImageFrameForCropPrep,
  clampImageToHostBounds,
  cropOutputExtension,
  cropOutputMime,
} from "./cropPrepModel";

export {
  CROP_OUTPUT_FORMATS,
  CROP_ZOOM_DEFAULT,
  CROP_ZOOM_MAX,
  CROP_ZOOM_MIN,
  clampCropZoom,
  cropOutputExtension,
  cropOutputMime,
};

export const CIRCLE_CROP_FIXED_FRAME = { x: 0, y: 0, w: 1, h: 1 };

export const CIRCLE_CROP_BG_TYPES = {
  TRANSPARENT: "transparent",
  BLUR: "blur",
  MULTICOLOR: "multicolor",
  WHITE: "white",
  BLACK: "black",
  CUSTOM: "custom",
};

export const CIRCLE_CROP_BG_OPTIONS = [
  { id: CIRCLE_CROP_BG_TYPES.TRANSPARENT, label: "Transparent" },
  { id: CIRCLE_CROP_BG_TYPES.BLUR, label: "Blur" },
  { id: CIRCLE_CROP_BG_TYPES.MULTICOLOR, label: "Multi color" },
  { id: CIRCLE_CROP_BG_TYPES.WHITE, label: "White" },
  { id: CIRCLE_CROP_BG_TYPES.BLACK, label: "Black" },
  { id: CIRCLE_CROP_BG_TYPES.CUSTOM, label: "Custom color" },
];

export const CIRCLE_CROP_BORDER_MIN = 0;
export const CIRCLE_CROP_BORDER_MAX = 20;
export const CIRCLE_CROP_BORDER_DEFAULT = 1;

export const CIRCLE_CROP_ROTATE_MIN = 0;
export const CIRCLE_CROP_ROTATE_MAX = 360;
export const CIRCLE_CROP_ROTATE_DEFAULT = 0;

export const CIRCLE_CROP_BORDER_COLOR_OPTIONS = [
  { id: "white", color: "#ffffff", label: "White" },
  { id: "black", color: "#000000", label: "Black" },
  { id: "indigo", color: "#4f46e5", label: "Indigo" },
  { id: "red", color: "#ef4444", label: "Red" },
  { id: "blue", color: "#2563eb", label: "Blue" },
];

function normalizeCircleCropHexColor(color) {
  const raw = String(color || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "white") return "#ffffff";
  if (raw === "black") return "#000000";
  if (/^#[0-9a-f]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`;
  }
  return raw;
}

export function isCircleCropBorderPresetColor(color) {
  const normalized = normalizeCircleCropHexColor(color);
  return CIRCLE_CROP_BORDER_COLOR_OPTIONS.some(
    (opt) => normalizeCircleCropHexColor(opt.color) === normalized
  );
}

/** Preview + export gradient for multi-color background. */
export const CIRCLE_CROP_MULTICOLOR_GRADIENT =
  "conic-gradient(from 210deg, #e8837a 0deg, #f5d0a8 72deg, #c9e4a0 144deg, #a8d4f0 216deg, #d4a8f0 288deg, #e8837a 360deg)";

export function clampCircleCropBorderWidth(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return CIRCLE_CROP_BORDER_DEFAULT;
  return Math.max(
    CIRCLE_CROP_BORDER_MIN,
    Math.min(CIRCLE_CROP_BORDER_MAX, Math.round(n))
  );
}

export function clampCircleCropRotation(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return CIRCLE_CROP_ROTATE_DEFAULT;
  return Math.max(
    CIRCLE_CROP_ROTATE_MIN,
    Math.min(CIRCLE_CROP_ROTATE_MAX, Math.round(n))
  );
}

export function normalizeCircleCropBackgroundType(type) {
  const valid = Object.values(CIRCLE_CROP_BG_TYPES);
  return valid.includes(type) ? type : CIRCLE_CROP_BG_TYPES.TRANSPARENT;
}

export function resolveCircleCropBackgroundColor(settings) {
  const type = normalizeCircleCropBackgroundType(settings?.backgroundType);
  if (type === CIRCLE_CROP_BG_TYPES.WHITE) return "#ffffff";
  if (type === CIRCLE_CROP_BG_TYPES.BLACK) return "#000000";
  if (type === CIRCLE_CROP_BG_TYPES.CUSTOM) {
    return settings?.backgroundColor || "#ffffff";
  }
  return null;
}

export function resolveCircleCropBorderColor(settings) {
  const preset = String(settings?.borderColor || "#ffffff").toLowerCase();
  if (preset === "custom") {
    return settings?.borderColorCustom || "#2563eb";
  }
  if (isCircleCropBorderPresetColor(preset)) {
    return normalizeCircleCropHexColor(preset);
  }
  if (preset.startsWith("#")) {
    return normalizeCircleCropHexColor(preset);
  }
  return settings?.borderColorCustom || settings?.borderColor || "#ffffff";
}

export function isCircleCropBorderColorCustom(settings) {
  const color = String(settings?.borderColor || "").toLowerCase();
  if (color === "custom") return true;
  return !isCircleCropBorderPresetColor(color);
}

/** Keep crop window square in display pixels (required for true circle mask). */
export function enforceSquareHostCrop(
  cropFrame,
  hostWidth,
  hostHeight,
  imageFrame = DEFAULT_IMAGE_FRAME
) {
  const aw = Math.max(1, Number(hostWidth) || 1);
  const ah = Math.max(1, Number(hostHeight) || 1);
  const image = normalizeImageFrame(imageFrame);
  const crop = normalizeHostCropFrame(cropFrame);
  const sidePx = Math.min(crop.w * aw, crop.h * ah);
  const w = sidePx / aw;
  const h = sidePx / ah;
  const cx = crop.x + crop.w / 2;
  const cy = crop.y + crop.h / 2;
  let x = cx - w / 2;
  let y = cy - h / 2;
  x = Math.max(image.x, Math.min(image.x + image.w - w, x));
  y = Math.max(image.y, Math.min(image.y + image.h - h, y));
  if (x + w > image.x + image.w) x = image.x + image.w - w;
  if (y + h > image.y + image.h) y = image.y + image.h - h;
  return clampCropInsideImage({ x, y, w, h }, image);
}

export function fitSquareCropInHost(
  fromFrame,
  hostWidth,
  hostHeight,
  imageFrame = DEFAULT_IMAGE_FRAME
) {
  const image = normalizeImageFrame(imageFrame);
  const aw = Math.max(1, Number(hostWidth) || 1);
  const ah = Math.max(1, Number(hostHeight) || 1);
  const maxSidePx = Math.min(image.w * aw, image.h * ah);
  const w = maxSidePx / aw;
  const h = maxSidePx / ah;
  const base = fromFrame ? normalizeHostCropFrame(fromFrame) : null;
  const cx = base ? base.x + base.w / 2 : image.x + image.w / 2;
  const cy = base ? base.y + base.h / 2 : image.y + image.h / 2;
  return enforceSquareHostCrop(
    { x: cx - w / 2, y: cy - h / 2, w, h },
    hostWidth,
    hostHeight,
    image
  );
}

/** Normalized w/h lock so crop box stays square on screen. */
export function resolveCircleHostAspectRatio(hostWidth, hostHeight) {
  const aw = Math.max(1, Number(hostWidth) || 1);
  const ah = Math.max(1, Number(hostHeight) || 1);
  return ah / aw;
}

export function createDefaultCircleCropPrepSettings() {
  return {
    zoom: CROP_ZOOM_DEFAULT,
    outputFormat: "png",
    imageFrame: { ...DEFAULT_IMAGE_FRAME },
    cropFrame: { ...CIRCLE_CROP_FIXED_FRAME },
    backgroundType: CIRCLE_CROP_BG_TYPES.TRANSPARENT,
    backgroundColor: "#ffffff",
    borderWidth: CIRCLE_CROP_BORDER_DEFAULT,
    borderColor: "#ffffff",
    borderColorCustom: "#2563eb",
    rotation: CIRCLE_CROP_ROTATE_DEFAULT,
    flipH: false,
    flipV: false,
  };
}

export function fitCircleCropFrameForSettings(
  settings,
  fromFrame = null,
  hostWidth = 0,
  hostHeight = 0
) {
  const imageFrame = settings?.imageFrame || DEFAULT_IMAGE_FRAME;
  if (hostWidth > 0 && hostHeight > 0) {
    return fitSquareCropInHost(fromFrame, hostWidth, hostHeight, imageFrame);
  }
  return clampCropInsideImage(
    fitCropRectToAspect(1, fromFrame || DEFAULT_CROP_RECT),
    imageFrame
  );
}

export function clampCircleCropInsideVisibleImage(
  cropFrame,
  imageFrame,
  hostWidth = 0,
  hostHeight = 0
) {
  let crop = clampCropInsideVisibleImage(cropFrame, imageFrame);
  if (hostWidth > 0 && hostHeight > 0) {
    crop = enforceSquareHostCrop(crop, hostWidth, hostHeight, imageFrame);
    crop = clampCropInsideHostViewport(crop);
    crop = clampCropInsideImage(crop, imageFrame);
  }
  return crop;
}

export function isCircleCropImageSmallerThanHost(
  imageWidth,
  imageHeight,
  hostWidth,
  hostHeight
) {
  const iw = Math.max(1, Number(imageWidth) || 1);
  const ih = Math.max(1, Number(imageHeight) || 1);
  const hw = Math.max(1, Number(hostWidth) || 1);
  const hh = Math.max(1, Number(hostHeight) || 1);
  return iw <= hw && ih <= hh;
}

/** Pan image inside the fixed crop without forcing cover when image is smaller than host. */
export function clampCircleCropImageFrame(imageFrame, cropFrame) {
  const crop = normalizeHostCropFrame(cropFrame);
  const img = normalizeImageFrame(imageFrame);

  if (img.w < crop.w - 1e-6 || img.h < crop.h - 1e-6) {
    let { x, y, w, h } = clampImageToHostBounds(img);
    x = Math.min(crop.x, Math.max(crop.x + crop.w - w, x));
    y = Math.min(crop.y, Math.max(crop.y + crop.h - h, y));
    return normalizeImageFrame({ x, y, w, h });
  }

  return clampImageFrameForCropPrep(img, crop);
}

export function moveCircleCropImageFrame(startFrame, dx, dy, cropFrame) {
  const start = normalizeImageFrame(startFrame);
  return clampCircleCropImageFrame(
    normalizeImageFrame({
      x: start.x + dx,
      y: start.y + dy,
      w: start.w,
      h: start.h,
    }),
    cropFrame
  );
}

export function resolveCircleCropPrepFrames(
  cropFrame,
  imageFrame,
  hostWidth = 0,
  hostHeight = 0
) {
  let crop = normalizeHostCropFrame(cropFrame);
  if (hostWidth > 0 && hostHeight > 0) {
    crop = enforceSquareHostCrop(crop, hostWidth, hostHeight, imageFrame);
  }
  const img = clampCircleCropImageFrame(imageFrame, crop);
  return { cropFrame: crop, imageFrame: img };
}

/** Fit image to crop host — original pixel size when image is smaller than canvas. */
export function fitImageFrameToCircleCropHost(
  cropFrame = CIRCLE_CROP_FIXED_FRAME,
  imageWidth = 1,
  imageHeight = 1,
  hostWidth = 0,
  hostHeight = 0
) {
  const crop = normalizeHostCropFrame(cropFrame);
  const iw = Math.max(1, Number(imageWidth) || 1);
  const ih = Math.max(1, Number(imageHeight) || 1);

  if (
    hostWidth > 0 &&
    hostHeight > 0 &&
    isCircleCropImageSmallerThanHost(iw, ih, hostWidth, hostHeight)
  ) {
    const w = iw / hostWidth;
    const h = ih / hostHeight;
    const x = crop.x + (crop.w - w) / 2;
    const y = crop.y + (crop.h - h) / 2;
    return clampCircleCropImageFrame(
      normalizeImageFrame({ x, y, w, h }),
      crop
    );
  }

  return fitImageFrameToCoverHostCrop(cropFrame, imageWidth, imageHeight);
}

export function fitImageFrameToCoverHostCrop(
  cropFrame = CIRCLE_CROP_FIXED_FRAME,
  imageWidth = 1,
  imageHeight = 1
) {
  const crop = normalizeHostCropFrame(cropFrame);
  const iw = Math.max(1, Number(imageWidth) || 1);
  const ih = Math.max(1, Number(imageHeight) || 1);
  const imageAspect = iw / ih;
  const cropAspect = crop.w / Math.max(crop.h, 1e-6);

  let w;
  let h;
  let x;
  let y;

  if (imageAspect > cropAspect) {
    h = crop.h;
    w = crop.h * (imageAspect / cropAspect);
    x = crop.x + (crop.w - w) / 2;
    y = crop.y;
  } else {
    w = crop.w;
    h = crop.w / (imageAspect / cropAspect);
    x = crop.x;
    y = crop.y + (crop.h - h) / 2;
  }

  return clampImageFrameForCropPrep(
    normalizeImageFrame({ x, y, w, h }),
    crop
  );
}

export function computeCircleImageFrameForZoom(
  zoomPercent,
  cropFrame,
  prevImageFrame = DEFAULT_IMAGE_FRAME,
  coverFrame = null
) {
  const scale = clampCropZoom(zoomPercent) / 100;
  const crop = normalizeHostCropFrame(cropFrame);
  const baseCover = normalizeImageFrame(
    coverFrame != null ? coverFrame : prevImageFrame
  );

  if (scale <= 1) {
    return clampCircleCropImageFrame({ ...baseCover }, crop);
  }

  const cx = crop.x + crop.w / 2;
  const cy = crop.y + crop.h / 2;
  const relX = baseCover.w > 0 ? (cx - baseCover.x) / baseCover.w : 0.5;
  const relY = baseCover.h > 0 ? (cy - baseCover.y) / baseCover.h : 0.5;
  const w = baseCover.w * scale;
  const h = baseCover.h * scale;
  const x = cx - relX * w;
  const y = cy - relY * h;
  return clampCircleCropImageFrame(
    normalizeImageFrame({ x, y, w, h }),
    crop
  );
}

export function getCircleCropSourceRect(settings) {
  const resolved = resolveCircleCropPrepFrames(
    settings?.cropFrame || DEFAULT_CROP_RECT,
    settings?.imageFrame || DEFAULT_IMAGE_FRAME
  );
  return hostFramesToSourceRect(resolved.cropFrame, resolved.imageFrame);
}

export function hasActiveCircleCrop(settings) {
  return !isFullCropRect(getCircleCropSourceRect(settings));
}
